import { join, resolve, dirname } from 'path';
import Store from '@/store';
import {
  StateAwareRouteDefinition,
  RouteDefinitionParts,
  AppState,
  RouteStateParams,
  UapInfo,
  SerializedResource,
} from '@/types';
import Directory from '@/structure/Resources/Directory';
import Banner from '@/structure/Resources/Banner';
import Video from '@/structure/Resources/Video';
import AbstractResource from '@structure/Resources/AbstractResource';

export const resolvePathFromSegments = (...segments: string[]) =>
  resolve(join(...segments));

export const parseDirectory = (path: string): string => {
  if (path.length === 0) {
    return '';
  }

  if (/index(_[a-z]+)?\.html$/i.test(path)) {
    return dirname(dirname(path));
  }

  return /\.[a-z\d]+$/i.test(path) ? dirname(path) : path;
};

const getSetting = (
  setting: string,
  state: AppState,
  query: object = {}
): boolean => {
  if (state.stuckSettings.includes(setting)) {
    return state[setting];
  }

  if (Object.prototype.hasOwnProperty.call(query, setting)) {
    return !!query[setting];
  }

  return state[setting];
};

export const applyStatetoRoute = (
  definition: RouteDefinitionParts,
  state: RouteStateParams
): StateAwareRouteDefinition => {
  const query = { ...definition.query, ...state };

  return {
    path: definition.path,
    query: {
      ...query,
      lights: Number(query.lights),
      sidebar: Number(query.sidebar),
    },
  };
};

/**
 * The state's `date` property is intentionally omitted from the route definition
 * built here. It should only persist when explicitly set on this method's
 * `query` parameter (used when applying a time travel timestamp).
 */
export const stateAwareRouteDefinition = ({
  path,
  query,
}: RouteDefinitionParts = {}): StateAwareRouteDefinition => {
  const { state, getters } = Store;

  const definition = applyStatetoRoute(
    { path, query },
    {
      lights: getSetting('lights', state, query),
      sidebar: getSetting('sidebar', state, query),
    }
  );

  if (path !== undefined) {
    return definition;
  }

  return {
    ...definition,
    path: getters.currentResource.path(),
  };
};

export const resourceRouteDefinition = (
  resource: AbstractResource
): StateAwareRouteDefinition =>
  stateAwareRouteDefinition({
    path: resource.path(),
    query: {
      lights: resource.config().lights(),
      sidebar: resource.config().sidebar(),
    },
  });

export const stateAwareUrl = (resource: AbstractResource): string => {
  const currentState = Store.state;
  const definition = resourceRouteDefinition(resource);
  const url = new URL(definition.path, window.location.origin);

  if (resource instanceof Banner && resource.config().timetravel()) {
    definition.query.date = currentState.time.sinceEpoch;
    definition.query.tz = currentState.time.zone;
  }

  const keys = Object.keys(definition.query);
  for (const key of keys) {
    url.searchParams.set(key, definition.query[key]);
  }

  return url.toString();
};

export const stateAwarePath = (resource: AbstractResource): string => {
  const url = new URL(stateAwareUrl(resource));

  return [url.pathname, url.search].join('');
};

export const ESPN_UAP_SIZE_MAPPING = [
  {
    width: 1280,
    height: 100,
    label: 'Extra Large',
    abbreviation: 'XL',
  },
  {
    width: 970,
    height: 66,
    label: 'Large',
    abbreviation: 'L',
  },
  {
    width: 728,
    height: 90,
    label: 'Medium',
    abbreviation: 'M',
  },
  {
    width: 320,
    height: 50,
    label: 'Small',
    abbreviation: 'S',
  },
] as UapInfo[];

export const serviceUrl = (parts: string[]): URL => {
  const trimSlashes = (str: string): string => str.replace(/(^\/|\/$)/g, '');

  const base = trimSlashes(process.env.VUE_APP_SERVICE_BASE as string);
  const path = parts.map(trimSlashes).join('/');

  return new URL(`${base}/${path}`);
};

export const resolveResourceFromConfig = (
  config: SerializedResource,
  parent: Directory
) => {
  switch (config.type) {
    case 'directory':
      return new Directory(config, parent);
    case 'video':
      return new Video(config, parent);
    default:
      return new Banner(config, parent);
  }
};
